import React from 'react';
import { DataTable, columnTypes, styles, number, era, whip, avg, obp, slg, ops, lob, percent, ratio, innings } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RVPerN, hitFXERA } from '../../../../utils/stats';
import StatTooltip from '../../../../components/StatTooltip';

const StyledSuperHeaderCell = styled.th`
    && {
        background-color: ${styles.theme.background.darkblue} !important;
        color: white;
    }
`;

const getNormalizedLevelAverages = (seasonStats, levelAverage) => {
    const { tbf: latestSeasonTbf } = seasonStats.filter((ss) => ss.tbf && ss.startyear === ss.endyear).reduce((maxSeason, season) => {
        if (season.startyear > maxSeason.startyear) {
            return season;
        }
        return maxSeason;
    }, { startyear: Number.MIN_SAFE_INTEGER });

    const normalizedLevelAverages = { ...levelAverage };
    Object.keys(normalizedLevelAverages).filter((k) => !['year'].includes(k)).forEach((k) => {
        normalizedLevelAverages[k] *= latestSeasonTbf / levelAverage.tbf;
    });

    return { normalizedLevelAverages, latestSeasonTbf };
};

const Table = ({ className, seasonStats, levelAverage, level, isBatterHandSelected }) => {
    const { t: translate } = useTranslation();

    const { normalizedLevelAverages, latestSeasonTbf } = getNormalizedLevelAverages(seasonStats, levelAverage);

    const allRows = [...seasonStats, {
        ...normalizedLevelAverages,
        isAvg: true,
    }];

    const columns = [{
        id: 'title',
        title: translate('common.date'),
        type: columnTypes.NODE,
        transform: (v) => (v.isAvg && levelAverage != null ? (
            <>
                {`'${levelAverage.yearid.toString().substring(2)} ${level.name} AVG`} <sub>({latestSeasonTbf}) TBF</sub>
            </>
        ) : v.title),
    }, {
        id: 'level',
        title: translate('baseball.level_one'),
        type: columnTypes.NODE,
        transform: () => level.name,
    }, {
        id: 'g',
        title: translate('baseball.abbr-games'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected || v.g === undefined || v.g === null ? '--' : v.g }</>,
        statistic: 'G',
    }, {
        id: 'gs',
        title: translate('baseball.abbr-gs'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected || v.gs === undefined || v.gs === null ? '--' : v.gs}</>,
        statistic: 'GS',
    }, {
        id: 'outs',
        title: translate('baseball.abbr-inning-pitched'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected || v.outs === null ? '--' : number(innings(v.outs), 1)}</>,
        statistic: 'IP',
    }, {
        id: 'tbf',
        title: translate('sections.reports.pitcher.abbr-tot-batter-faced'),
        type: columnTypes.NUMBER,
        statistic: 'BF',
    }, {
        id: 'pitches',
        title: translate('sections.reports.pitcher.abbr-num-pitches'),
        type: columnTypes.NUMBER,
        statistic: 'Pitches',
    }, {
        id: 'hits',
        title: translate('baseball.abbr-hits'),
        type: columnTypes.NUMBER,
        statistic: 'H',
    }, {
        id: 'r',
        title: translate('baseball.abbr-runs'),
        type: columnTypes.NUMBER,
        statistic: 'R',
    }, {
        id: 'er',
        title: translate('baseball.abbr-earned-runs'),
        type: columnTypes.NUMBER,
        statistic: 'ER',
    }, {
        id: 'b2',
        title: translate('baseball.2b'),
        type: columnTypes.NUMBER,
        statistic: '2B',
    }, {
        id: 'b3',
        title: translate('baseball.3b'),
        type: columnTypes.NUMBER,
        statistic: '3B',
    }, {
        id: 'hr',
        title: translate('baseball.abbr-homerun'),
        type: columnTypes.NUMBER,
        statistic: 'HR',
    }, {
        id: 'so',
        title: translate('baseball.abbr-strikeout-k'),
        type: columnTypes.NUMBER,
        statistic: 'SO',
    }, {
        id: 'ubb',
        title: translate('baseball.abbr-walk'),
        type: columnTypes.NUMBER,
        statistic: 'UBB',
    }, {
        id: 'ibb',
        title: translate('baseball.abbr-walk-ibb'),
        type: columnTypes.NUMBER,
        statistic: 'IBB',
    }, {
        id: 'hbp',
        title: translate('baseball.abbr-hbp'),
        type: columnTypes.NUMBER,
        statistic: 'HBP',
    }, {
        id: 'wp',
        title: translate('baseball.abbr-wp'),
        type: columnTypes.NUMBER,
        statistic: 'WP',
    }, {
        id: 'bk',
        title: translate('baseball.abbr-bk'),
        type: columnTypes.NUMBER,
        statistic: 'BK',
    }, {
        id: 'sb',
        title: translate('baseball.abbr-stolen-base'),
        type: columnTypes.NUMBER,
        statistic: 'SB',
    }, {
        id: 'cs',
        title: translate('baseball.abbr-cs'),
        type: columnTypes.NUMBER,
        statistic: 'CS',
    }, {
        id: 'era',
        title: translate('baseball.abbr-era'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected ? '--' : number(era(v.er, v.outs), 2) || '--'}</>,
        statistic: 'ERA',
    }, {
        id: 'whip',
        title: translate('baseball.abbr-whip'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected ? '--' : number(whip(v.b1, v.b2, v.b3, v.hr, v.ubb, v.outs), 2) || '--'}</>,
        statistic: 'WHIP',
    }, {
        id: 'ba',
        title: translate('baseball.abbr-ba'),
        type: columnTypes.NODE,
        transform: (v) => number(avg(v.b1, v.b2, v.b3, v.hr, v.ab), true),
        statistic: 'AVG',
    }, {
        id: 'obp',
        title: translate('baseball.abbr-obp'),
        type: columnTypes.NODE,
        transform: (v) => number(obp(v.b1, v.b2, v.b3, v.hr, v.ibb, v.ubb, v.hbp, v.sf, v.ab), true),
        statistic: 'OBP',
    }, {
        id: 'slg',
        title: translate('baseball.abbr-slugging'),
        type: columnTypes.NODE,
        transform: (v) => number(slg(v.b1, v.b2, v.b3, v.hr, v.ab), true),
        statistic: 'SLG',
    }, {
        id: 'ops',
        title: translate('baseball.abbr-ops'),
        type: columnTypes.NODE,
        transform: (v) => number(ops(v.b1, v.b2, v.b3, v.hr, v.ibb, v.ubb, v.hbp, v.sf, v.ab), true),
        statistic: 'OPS',
    }, {
        id: 'inhr',
        title: translate('baseball.abbr-ir'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected || v.inhr === null ? '--' : number(v.inhr, 0) }</>,
        statistic: 'IR',
    }, {
        id: 'inhrs',
        title: translate('baseball.abbr-irs'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected || v.inhrs === null ? '--' : number(v.inhrs, 0) }</>,
        statistic: 'IRS',
    }, {
        id: 'beqr',
        title: translate('baseball.abbr-bqr'),
        type: columnTypes.NODE,
        transform: (v) => <>{isBatterHandSelected || v.beqr === null ? '--' : number(v.beqr, 0)}</>,
        statistic: 'BqR',
    }, {
        id: 'beqrs',
        title: translate('baseball.abbr-bqrs'),
        type: columnTypes.NUMBER,
        statistic: 'BqRS',
    }, {
        id: 'lob',
        title: translate('baseball.abbr-lob-percent'),
        type: columnTypes.NODE,
        transform: (v) => (v.tbf === null ? null : percent(lob(v.b1, v.b2, v.b3, v.hr, v.ubb, v.ibb, v.hbp, v.r), 0)),
        statistic: 'LOBPercent',
    }, {
        id: 'hitfx ERA',
        title: translate('sections.reports.pitcher.hitfx-era'),
        headerClass: 'border-start',
        cellClass: 'border-start',
        type: columnTypes.NODE,
        transform: (v) => number(hitFXERA(v.rvhitfxpit, v.pawrv), 2),
        statistic: 'HitFX2ERA',
    }, {
        id: 'bb%',
        title: translate('sections.reports.pitcher.abr-walk-pct'),
        type: columnTypes.NODE,
        transform: (v) => (v.tbf === null ? null : percent(ratio(v.ubb, v.pa), 1)),
        statistic: 'UBBPercent',
    }, {
        id: 'k%',
        title: translate('sections.reports.pitcher.abbr-strikeout-pct'),
        type: columnTypes.NODE,
        transform: (v) => (v.tbf === null ? null : percent(ratio(v.so, v.pa), 1)),
        statistic: 'KPercent',
    }, {
        id: 'contactQuality',
        title: translate('sections.reports.pitcher.contact-quality'),
        type: columnTypes.NODE,
        transform: (v) => number(RVPerN(v.rvhitfxpitbip, v.biprvdenom, 100), 1),
        statistic: 'ContactQuality',
    }];

    const superheader = (
        <tr>
            <StyledSuperHeaderCell colSpan={32}>{translate('sections.reports.pitcher.season-stats')}</StyledSuperHeaderCell>
            <StyledSuperHeaderCell
                className="border-start"
                colSpan={4}
            >{translate('sections.reports.pitcher.overall-performance')}
            </StyledSuperHeaderCell>
        </tr>
    );

    return (
        <div className={`${className} text-center`}>
            <DataTable
                statTooltip={StatTooltip}
                columns={columns}
                rows={allRows}
                superheader={superheader}
                rowClass={({ isAvg }) => (isAvg ? 'fw-bold border-top' : '')}
            />
        </div>
    );
};

export default Table;
